import { useSessionStore } from "@/application/stores/sessionStore";
import type { NavKey } from "@/features/platform/components/Sidenav";

type RoleName = "SYSTEM_ADMIN" | "SUPERVISOR" | "AUXILIARY" | "OPERATOR" | "VIEWER";

/** Nav keys each role is allowed to see */
const NAV_ACCESS: Record<RoleName, NavKey[] | "*"> = {
  SYSTEM_ADMIN: "*",
  SUPERVISOR: [
    "warehouses",
    "products",
    "inventory",
    "inventory.stock",
    "inventory.kardex",
    "inventory.receipts",
    "inventory.dispatches",
    "inventory.transfers",
    "inventory.balance",
    "inventory.adjustments",
    "settings",
    "settings.typeUse",
    "settings.typeUnit",
    "settings.movementSubtype",
    "settings.unitConversion",
    "settings.category",
    "admin",
    "admin.usage",
  ],
  AUXILIARY: [
    "warehouses",
    "products",
    "inventory",
    "inventory.stock",
    "inventory.kardex",
    "inventory.receipts",
    "inventory.dispatches",
    "inventory.transfers",
    "inventory.balance",
    "inventory.adjustments",
    "settings",
    "settings.typeUse",
    "settings.typeUnit",
    "settings.movementSubtype",
    "settings.unitConversion",
  ],
  OPERATOR: [
    "warehouses",
    "products",
    "inventory",
    "inventory.stock",
    "inventory.receipts",
    "inventory.dispatches",
    "inventory.transfers",
    "inventory.balance",
    "inventory.adjustments",
    "settings",
    "settings.typeUse",
    "settings.typeUnit",
    "settings.movementSubtype",
    "settings.unitConversion",
  ],
  VIEWER: [
    "warehouses",
    "products",
    "inventory",
    "inventory.stock",
    "inventory.receipts",
    "inventory.dispatches",
    "inventory.transfers",
    "inventory.balance",
    "inventory.adjustments",
    "settings",
    "settings.typeUse",
    "settings.typeUnit",
    "settings.movementSubtype",
    "settings.unitConversion",
  ],
};

export function useRole() {
  const roleName = (useSessionStore(s => s.roleName) || "VIEWER") as RoleName;

  const allowedNavKeys = NAV_ACCESS[roleName];

  /** Returns true if the given nav key is visible for this role */
  const canSeeNav = (key: NavKey): boolean => {
    if (allowedNavKeys === "*") return true;
    return allowedNavKeys.includes(key);
  };

  // --- Granular per-module capabilities ---
  // Warehouses: create/edit only admin & supervisor
  const canCreateWarehouse = ["SYSTEM_ADMIN", "SUPERVISOR"].includes(roleName);
  const canEditWarehouse = ["SYSTEM_ADMIN", "SUPERVISOR"].includes(roleName);

  // Products: create/edit admin, supervisor & auxiliary
  const canCreateProduct = ["SYSTEM_ADMIN", "SUPERVISOR", "AUXILIARY"].includes(roleName);
  const canEditProduct = ["SYSTEM_ADMIN", "SUPERVISOR", "AUXILIARY"].includes(roleName);

  // Type Use: create/edit only admin & supervisor
  const canCreateTypeUse = ["SYSTEM_ADMIN", "SUPERVISOR"].includes(roleName);
  const canEditTypeUse = ["SYSTEM_ADMIN", "SUPERVISOR"].includes(roleName);

  // Type Unit: create/edit only admin & supervisor
  const canCreateTypeUnit = ["SYSTEM_ADMIN", "SUPERVISOR"].includes(roleName);
  const canEditTypeUnit = ["SYSTEM_ADMIN", "SUPERVISOR"].includes(roleName);

  // Movement Subtype: create/edit only admin & supervisor
  const canCreateMovementSubtype = ["SYSTEM_ADMIN", "SUPERVISOR"].includes(roleName);
  const canEditMovementSubtype = ["SYSTEM_ADMIN", "SUPERVISOR"].includes(roleName);

  // Unit Conversion: create/edit only admin & supervisor
  const canCreateUnitConversion = ["SYSTEM_ADMIN", "SUPERVISOR"].includes(roleName);
  const canEditUnitConversion = ["SYSTEM_ADMIN", "SUPERVISOR"].includes(roleName);

  // Category: create/edit only admin & supervisor
  const canCreateCategory = ["SYSTEM_ADMIN", "SUPERVISOR"].includes(roleName);
  const canEditCategory = ["SYSTEM_ADMIN", "SUPERVISOR"].includes(roleName);

  // Inventory documents (receipts, dispatches, transfers, adjustments)
  const canCreateDoc = ["SYSTEM_ADMIN", "SUPERVISOR", "AUXILIARY"].includes(roleName);
  const canEditDoc = ["SYSTEM_ADMIN", "SUPERVISOR", "AUXILIARY"].includes(roleName);

  return {
    role: roleName,
    // Legacy generic flags (kept for inventory document pages)
    canCreate: canCreateDoc,
    canEdit: canEditDoc,
    canConfirm: ["SYSTEM_ADMIN", "SUPERVISOR"].includes(roleName),
    canCancel: ["SYSTEM_ADMIN", "SUPERVISOR"].includes(roleName),
    canApprove: ["SYSTEM_ADMIN", "SUPERVISOR"].includes(roleName),
    canSubmit: ["SYSTEM_ADMIN", "SUPERVISOR", "AUXILIARY"].includes(roleName),
    canVoid: roleName === "SYSTEM_ADMIN",
    canCancelTransfer: roleName === "SYSTEM_ADMIN",
    canClosePeriod: ["SYSTEM_ADMIN", "SUPERVISOR"].includes(roleName),
    isViewOnly: ["OPERATOR", "VIEWER"].includes(roleName),
    isAdmin: roleName === "SYSTEM_ADMIN",
    canSeeNav,
    // Granular per-module
    canCreateWarehouse,
    canEditWarehouse,
    canCreateProduct,
    canEditProduct,
    canCreateTypeUse,
    canEditTypeUse,
    canCreateTypeUnit,
    canEditTypeUnit,
    canCreateMovementSubtype,
    canEditMovementSubtype,
    canCreateUnitConversion,
    canEditUnitConversion,
    canCreateCategory,
    canEditCategory,
  };
}
